import { useRef, useState } from "react";
import { api } from "../api";

const pasteKey = () =>
  navigator.platform.toLowerCase().includes("mac") ? "\u2318V" : "Ctrl+V";

/** A chart image attached to a trade — what the setup actually looked like. */
export function Screenshot({
  tradeId,
  path,
  onChange,
}: {
  tradeId: number;
  path: string | null | undefined;
  onChange: (path: string | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(false);

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const { screenshot_path } = await api.uploadScreenshot(tradeId, file);
      onChange(screenshot_path);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="section-title">
        Chart Screenshot
        <button
          className="ghost"
          style={{ float: "right", padding: "1px 8px" }}
          disabled={busy}
          onClick={() => input.current?.click()}
        >
          {busy ? "Uploading…" : path ? "Replace" : "+ Add image"}
        </button>
      </div>

      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload(file);
          e.target.value = "";
        }}
      />

      {error && <div className="banner error">{error}</div>}

      {path ? (
        <div className="shot">
          <img
            src={`/api/screenshots/${path}`}
            alt="Chart screenshot"
            onClick={() => setZoom(true)}
          />
          <button
            className="ghost danger"
            onClick={async () => {
              await api.deleteScreenshot(tradeId);
              onChange(null);
            }}
          >
            Remove
          </button>
        </div>
      ) : (
        <div
          className="dropzone"
          tabIndex={0}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files?.[0];
            if (file) upload(file);
          }}
          // Paste is scoped to this element rather than the document: several
          // trade cards can be open at once, and a document-level listener
          // would have no way to know which one you meant.
          onPaste={(e) => {
            const item = [...e.clipboardData.items].find((i) =>
              i.type.startsWith("image/"),
            );
            const file = item?.getAsFile();
            if (file) {
              e.preventDefault();
              upload(file);
            }
          }}
          onClick={() => input.current?.click()}
        >
          <strong>Click here and press {pasteKey()}</strong> to paste a screenshot,
          <br />
          or drop an image, or click to browse. PNG, JPEG or WebP.
        </div>
      )}

      {zoom && path && (
        <div className="modal-backdrop" onClick={() => setZoom(false)}>
          <img className="shot-full" src={`/api/screenshots/${path}`} alt="Chart screenshot" />
        </div>
      )}
    </div>
  );
}
